import { AxiosResponse } from 'axios';
import { useAxios } from '@/features/common/helpers/axios';
import {
  SchedulePickupRequest,
  SchedulePickupResponse,
  UnscheduledPickups,
} from '@/features/schedule-pickup/types';
import i18n from '@/i18n';
import { GlobalResponseType } from '@/features/common/types';
import { parseError } from '@/features/common/helpers/errorParser';

export const { axiosService, baseURL } = useAxios();

export interface UrlQuery {
  offset?: number,
  limit?: number,
  orderBy?: string,
  orderByDesc?: boolean,
  search?: string | null,
  carriers?: string[],
  dateRange?: string[],
  isDontShowSharedUsers?: boolean,
}

export interface ReSchedulePickupResponseType extends GlobalResponseType {
  rescheduled?: boolean,
}

export const getUnscheduledPickupsService = async ({
  offset = 0,
  limit = 20,
  orderBy = 'Ship_DateTime',
  orderByDesc = true,
  search = null,
  carriers = [],
  dateRange = [],
  isDontShowSharedUsers = false,
}: UrlQuery = {}): Promise<UnscheduledPickups[] | null> => {
  try {
    let queryString = `?Offset=${offset}&Limit=${limit}&OrderBy=${orderBy}&OrderByDescending=${orderByDesc}`;
    if (search) {
      queryString += `&search=${encodeURI(search)}`;
    }
    if (carriers.length) {
      carriers.forEach((carrier) => {
        queryString += `&carriers=${carrier}`;
      });
    }
    if (dateRange.length) {
      const [fromDate, toDate] = dateRange;
      if (fromDate) {
        queryString += `&fromDate=${fromDate}`;
      }
      if (toDate) {
        queryString += `&toDate=${toDate}`;
      }
    }
    if (isDontShowSharedUsers) {
      queryString += `&isDontShowSharedUsers=${isDontShowSharedUsers}`;
    }

    const unscheduledPickupsRes: AxiosResponse = await axiosService.get(
      `/pickups/unscheduled${queryString}`,
    );
    return unscheduledPickupsRes.data ?? [];
  } catch (err) {
    return null;
  }
};

export const scheduleAPickup = async (
  data: SchedulePickupRequest,
  pageSourceUrl: string,
): Promise<SchedulePickupResponse | null> => {
  try {
    const response: AxiosResponse = await axiosService.post(
      '/pickups/schedule-pickup',
      data,
    );

    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      id: response.data.id,
      pickupRequestNumber: response.data.pickupRequestNumber,
      carrierPhone: response.data.carrierPhone,
      isCarrierMessage: false,
    };
  } catch (err) {
    const parsedError = parseError(err);

    let redirectUrl = '';
    let isExternalLink = false;
    let isCarrierMessage = false;
    const { errorType } = parsedError;
    let { errorMessage } = parsedError;

    switch (errorType) {
      case undefined:
      case null:
        errorMessage = i18n.t('alertDialog.somethingWentWrong') as string;
        break;
      case 'IsPrintDisabledException':
      case 'IsCreditCardValidException':
        redirectUrl = '/webship/paymentoptions.aspx';
        isExternalLink = true;
        break;
      case 'IsLoginDisabledException':
        redirectUrl = `/logout?redirectUrl=${pageSourceUrl}`;
        break;
      case 'TermsAndConditionsValidateException':
        redirectUrl = `/terms?redirectUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`;
        break;
      case 'CarrierException':
        isCarrierMessage = true;
        break;
      default:
        redirectUrl = '';
    }

    const messageKey = `scheduledPickups.errorMessages.${errorType}`;
    if (i18n.te(messageKey)) {
      errorMessage = i18n.t(messageKey) as string;
    }

    return {
      successful: false,
      errorMessage,
      redirectUrl,
      errorType,
      isCarrierMessage,
      isExternalLink,
    };
  }
};

export const reschedulePickup = async (
  trackingNumber: string,
): Promise<ReSchedulePickupResponseType | null> => {
  try {
    const res: AxiosResponse = await axiosService.put(`/pickups/reschedule-pickup/${trackingNumber}`);

    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      rescheduled: res.data ? res.data.rescheduled : null,
    };
  } catch (err) {
    const parsedError = parseError(err);

    let redirectUrl = '';
    const { errorType } = parsedError;
    let { errorMessage } = parsedError;

    switch (errorType) {
      case undefined:
      case null:
        errorMessage = i18n.t('alertDialog.somethingWentWrong') as string;
        break;
      default:
        redirectUrl = '';
    }

    const messageKey = `scheduledPickups.errorMessages.${errorType}`;
    if (i18n.te(messageKey)) {
      errorMessage = i18n.t(messageKey) as string;
    }

    return {
      successful: false,
      errorMessage,
      redirectUrl,
      errorType,
    };
  }
};

export const noPickupRequired = async (
  trackingNumbers: string[],
): Promise<GlobalResponseType | null> => {
  try {
    await axiosService.post(
      '/pickups/not-required',
      {
        trackingNumbers,
      },
    );

    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      isCarrierMessage: false,
    };
  } catch (err) {
    return parseError(err);
  }
};
